import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Slide,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Fab,
  Backdrop,
  Zoom,
  Grow,
} from "@mui/material";
import {
  QrCode2 as QrCodeIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  SwipeLeft as SwipeIcon,
  KeyboardArrowLeft as ArrowLeftIcon,
  KeyboardArrowRight as ArrowRightIcon,
} from "@mui/icons-material";
import QRCode from "qrcode";
import { Rental } from "../../types/api-types";
import { apiClient } from "../../services/apiClient";
import {
  buildRentalUrl,
  buildPdfDownloadUrl,
  buildPublicPdfUrl,
  getPosBaseUrl,
  isDevelopment,
} from "../../utils/environment";

interface EndRentalModalProps {
  open: boolean;
  rental: Rental;
  onClose: () => void;
  onConfirm: () => Promise<any>;
  calculation: any;
  endRentalResponse?: any;
}

// Sub-Komponente für Loading State
const LoadingView: React.FC<{
  error?: string;
  onRetry?: () => void;
  isProcessing?: boolean;
}> = ({ error, onRetry, isProcessing }) => (
  <Box sx={{ textAlign: "center", py: { xs: 4, md: 6 } }}>
    {error ? (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          color="error"
          onClick={onRetry}
          disabled={isProcessing}
          startIcon={<CheckIcon />}
        >
          Erneut versuchen
        </Button>
      </Box>
    ) : (
      <Box>
        <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
          <CircularProgress
            size={60}
            sx={{
              color: "primary.main",
              animationDuration: "1.5s",
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h5" sx={{ animation: "pulse 2s infinite" }}>
              ⚡
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          Vermietung wird beendet...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          📄 Rechnung wird automatisch erstellt
        </Typography>
      </Box>
    )}
  </Box>
);

// Sub-Komponente für QR-Code
const QRCodeCard: React.FC<{
  qrCodeData: string;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isMobile: boolean;
}> = ({ qrCodeData, canvasRef, isMobile }) => (
  <Card
    sx={{
      height: "100%",
      background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      border: "2px solid",
      borderColor: "info.main",
      borderRadius: { xs: 3, md: 2 },
      transition: "all 0.3s ease",
      "&:hover": {
        transform: isMobile ? "none" : "translateY(-4px)",
        boxShadow: isMobile ? "none" : 6,
      },
    }}
  >
    <CardContent
      sx={{
        textAlign: "center",
        p: { xs: 1, md: 1 },
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          border: "2px solid #0288d1",
          borderRadius: "12px",
          width: isMobile ? "80%" : "80%",
          height: isMobile ? "80%" : "80%",
          maxWidth: "280px",
          maxHeight: "280px",
        }}
      />
    </CardContent>
  </Card>
);

// Sub-Komponente für PDF
const PDFCard: React.FC<{
  pdfUrl: string;
  onShare: () => void;
  isMobile: boolean;
}> = ({ pdfUrl, onShare, isMobile }) => (
  <Card
    sx={{
      height: "100%",
      background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
      border: "2px solid",
      borderColor: "error.main",
      borderRadius: { xs: 3, md: 2 },
      transition: "all 0.3s ease",
      "&:hover": {
        transform: isMobile ? "none" : "translateY(-4px)",
        boxShadow: isMobile ? "none" : 6,
      },
    }}
  >
    <CardContent sx={{ textAlign: "center", p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: isMobile ? "120px" : "140px",
          mb: 2,
          bgcolor: "white",
          borderRadius: 2,
          border: "2px solid",
          borderColor: "error.main",
        }}
      >
        <PdfIcon sx={{ fontSize: isMobile ? 48 : 60, color: "error.main" }} />
      </Box>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        gutterBottom
        sx={{ color: "error.main", fontWeight: 600 }}
      >
        📄 Rechnung
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: isMobile ? "0.8rem" : "0.9rem" }}
      >
        PDF herunterladen & teilen
      </Typography>
    </CardContent>
    <CardActions sx={{ justifyContent: "center", pb: 2, px: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Button
          variant="contained"
          color="error"
          size={isMobile ? "medium" : "small"}
          startIcon={<DownloadIcon />}
          href={pdfUrl}
          target="_blank"
          fullWidth={isMobile}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Öffnen
        </Button>
        <Button
          variant="outlined"
          color="error"
          size={isMobile ? "medium" : "small"}
          startIcon={<ShareIcon />}
          onClick={onShare}
          fullWidth={isMobile}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Teilen
        </Button>
      </Box>
    </CardActions>
  </Card>
);

// Sub-Komponente für Close Action
const FinishCard: React.FC<{
  onClose: () => void;
  isMobile: boolean;
}> = ({ onClose, isMobile }) => (
  <Card
    sx={{
      height: "100%",
      background: "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)",
      border: "2px solid",
      borderColor: "success.main",
      borderRadius: { xs: 3, md: 2 },
      transition: "all 0.3s ease",
      "&:hover": {
        transform: isMobile ? "none" : "translateY(-4px)",
        boxShadow: isMobile ? "none" : 6,
      },
    }}
  >
    <CardContent sx={{ textAlign: "center", p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: isMobile ? "120px" : "140px",
          mb: 2,
          bgcolor: "white",
          borderRadius: 2,
          border: "2px solid",
          borderColor: "success.main",
        }}
      >
        <CheckIcon
          sx={{ fontSize: isMobile ? 48 : 60, color: "success.main" }}
        />
      </Box>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        gutterBottom
        sx={{ color: "success.main", fontWeight: 600 }}
      >
        ✅ Fertig
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: isMobile ? "0.8rem" : "0.9rem" }}
      >
        Zurück zum Dashboard
      </Typography>
    </CardContent>
    <CardActions sx={{ justifyContent: "center", pb: 2 }}>
      <Button
        variant="contained"
        color="success"
        size={isMobile ? "large" : "medium"}
        onClick={onClose}
        fullWidth={isMobile}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          px: isMobile ? 4 : 3,
        }}
      >
        Schließen
      </Button>
    </CardActions>
  </Card>
);

// Mobile Swipe Navigation
const MobileNavigation: React.FC<{
  currentSlide: number;
  totalSlides: number;
  onPrevious: () => void;
  onNext: () => void;
}> = ({ currentSlide, totalSlides, onPrevious, onNext }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mt: 3,
      px: 2,
    }}
  >
    <Fab
      size="small"
      color="primary"
      onClick={onPrevious}
      disabled={currentSlide === 0}
      sx={{ opacity: currentSlide === 0 ? 0.3 : 1 }}
    >
      <ArrowLeftIcon />
    </Fab>

    <Box sx={{ display: "flex", gap: 1 }}>
      {Array.from({ length: totalSlides }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: index === currentSlide ? "primary.main" : "grey.300",
            transition: "all 0.3s ease",
          }}
        />
      ))}
    </Box>

    <Fab
      size="small"
      color="primary"
      onClick={onNext}
      disabled={currentSlide === totalSlides - 1}
      sx={{ opacity: currentSlide === totalSlides - 1 ? 0.3 : 1 }}
    >
      <ArrowRightIcon />
    </Fab>
  </Box>
);

const EndRentalModal: React.FC<EndRentalModalProps> = ({
  open,
  rental,
  onClose,
  onConfirm,
  calculation,
  endRentalResponse,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [step, setStep] = useState<"processing" | "success">("processing");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  const slides = [
    { type: "finish", component: FinishCard },
    { type: "qr", component: QRCodeCard },
    { type: "pdf", component: PDFCard },
  ];

  useEffect(() => {
    if (open && !isProcessing) {
      if (endRentalResponse) {
        console.log(
          "✅ EndRentalModal opened with existing response:",
          endRentalResponse
        );
        setStep("success");
        processEndRentalResponse(endRentalResponse);
      } else {
        console.log(
          "🚀 EndRentalModal opened - starting rental end process immediately"
        );
        resetStates();
        setStep("processing");
        setIsProcessing(true);
        handleEndRentalImmediate();
      }
    }
  }, [open, endRentalResponse]);

  const resetStates = () => {
    setQrCodeData("");
    setPdfUrl("");
    setInvoiceData(null);
    setError("");
    setCurrentSlide(0);
  };

  const processEndRentalResponse = (response: any) => {
    if (response?.qrCode) {
      const pdfUrl = buildPublicPdfUrl(response.qrCode);
      setQrCodeData(pdfUrl);
      setPdfUrl(pdfUrl);
      setInvoiceData(response.invoice);
      console.log("🔗 QR PDF URL:", pdfUrl);
      console.log("📄 PDF URL:", pdfUrl);
    }
  };

  const generateQRCode = async (
    data: string,
    canvasRef: React.RefObject<HTMLCanvasElement>
  ) => {
    if (canvasRef.current && data) {
      try {
        await QRCode.toCanvas(canvasRef.current, data, {
          width: 280,
          margin: 2,
          color: {
            dark: "#1976d2",
            light: "#ffffff",
          },
        });
      } catch (err) {
        console.error("Error generating QR code:", err);
      }
    }
  };

  useEffect(() => {
    if (qrCodeData && step === "success") {
      generateQRCode(qrCodeData, qrCanvasRef);
    }
  }, [qrCodeData, step, isMobile]);

  const loadGeneratedInvoiceAsFallback = async () => {
    try {
      console.log("⚠️ Using fallback: Loading invoice from API (inefficient!)");
      const response = await apiClient.getInvoices();
      const invoices = (response as any)?.data || response || [];

      if (!Array.isArray(invoices)) {
        console.warn("⚠️ API response is not an array:", invoices);
        throw new Error("Invalid invoices data format");
      }

      const rentalInvoice = invoices.find(
        (inv: any) => inv.rentalId === rental.id
      );

      if (rentalInvoice && rentalInvoice.qrCode) {
        console.log("✅ Invoice found with QR code:", rentalInvoice.qrCode);
        const pdfUrl = buildPublicPdfUrl(rentalInvoice.qrCode);
        setQrCodeData(pdfUrl);
        setPdfUrl(pdfUrl);
        setInvoiceData(rentalInvoice);
        console.log("🔗 QR PDF URL:", pdfUrl);
        console.log("📄 PDF URL:", pdfUrl);
      } else {
        console.warn("⚠️ No invoice found or no QR code available");
        const fallbackUrl = buildRentalUrl(rental.id);
        setQrCodeData(fallbackUrl);
      }
    } catch (error) {
      console.error("Error loading generated invoice:", error);
      const fallbackUrl = buildRentalUrl(rental.id);
      setQrCodeData(fallbackUrl);
    }
  };

  const handleEndRentalImmediate = async () => {
    console.log("🎯 Starting immediate rental end process");

    try {
      const result = await onConfirm();
      console.log("📊 EndRentalModal received result:", result);

      if (result && result.qrCode) {
        console.log("✅ Using invoice data from response");
        const pdfUrl = buildPublicPdfUrl(result.qrCode);
        setQrCodeData(pdfUrl);
        setPdfUrl(pdfUrl);
        console.log("🔗 QR PDF URL:", pdfUrl);
        console.log("📄 PDF URL (Download):", pdfUrl);
      } else {
        console.log("⚠️ No invoice data in response, trying to load from API");
        await loadGeneratedInvoiceAsFallback();
      }

      setStep("success");
    } catch (err: any) {
      console.error("❌ Error in immediate rental end:", err);
      setError(err.message || "Fehler beim Beenden der Vermietung");
      setStep("processing");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = async () => {
    console.log("🔍 handleShare called, pdfUrl:", pdfUrl);

    if (!pdfUrl) {
      console.error("❌ No pdfUrl available for sharing");
      alert(
        "PDF-URL ist nicht verfügbar. Bitte versuchen Sie es später erneut."
      );
      return;
    }

    console.log("📤 Attempting to share PDF:", pdfUrl);

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Rechnung - ${rental.object?.name}`,
          text: `Rechnung für Vermietung: ${rental.object?.name}`,
          url: pdfUrl,
        });
        console.log("📤 PDF shared successfully");
      } catch (err) {
        console.log("Share cancelled or failed", err);
        fallbackCopyToClipboard(
          pdfUrl,
          "PDF-Link wurde in die Zwischenablage kopiert"
        );
      }
    } else {
      console.log("📋 Using clipboard fallback for PDF sharing");
      fallbackCopyToClipboard(
        pdfUrl,
        "PDF-Link wurde in die Zwischenablage kopiert"
      );
    }
  };

  const fallbackCopyToClipboard = async (
    text: string,
    successMessage: string
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(successMessage);
    } catch (err) {
      console.error("Failed to copy to clipboard", err);
      window.prompt("Link kopieren:", text);
    }
  };

  const handleSlideChange = (direction: "next" | "prev") => {
    if (direction === "next" && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (direction === "prev" && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleRetry = () => {
    setError("");
    if (!isProcessing) {
      setIsProcessing(true);
      handleEndRentalImmediate();
    }
  };

  return (
    <>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          
          @keyframes slideInLeft {
            from { transform: translateX(-100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          @keyframes fadeInUp {
            from { transform: translateY(30px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>

      <Dialog
        open={open}
        onClose={step === "processing" ? undefined : onClose}
        maxWidth={isMobile ? "sm" : "lg"}
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 3 : 3,
            minHeight: isMobile ? "auto" : 500,
            background: isMobile
              ? "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)"
              : "white",
            m: isMobile ? 2 : 0, // Margin oben/unten links/rechts auf Smartphone
          },
        }}
        TransitionComponent={isMobile ? Slide : Grow}
        TransitionProps={isMobile ? ({ direction: "up" } as any) : undefined}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            background: isMobile
              ? "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"
              : "transparent",
            color: isMobile ? "white" : "inherit",
            p: { xs: 3, md: 4 },
            borderRadius: isMobile ? 0 : "24px 24px 0 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="div"
              sx={{
                fontWeight: 700,
                background: isMobile
                  ? "transparent"
                  : "linear-gradient(45deg, #1976d2, #42a5f5)",
                backgroundClip: isMobile ? "unset" : "text",
                WebkitBackgroundClip: isMobile ? "unset" : "text",
                WebkitTextFillColor: isMobile ? "white" : "transparent",
              }}
            >
              {step === "processing" ? "⚡ Wird beendet..." : "🎉 Beendet !"}
            </Typography>
            {step !== "processing" && (
              <IconButton
                onClick={onClose}
                size="large"
                sx={{
                  color: isMobile ? "white" : "inherit",
                  bgcolor: isMobile ? "rgba(255,255,255,0.1)" : "transparent",
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 2, md: 4 },
            overflow: isMobile ? "hidden" : "visible",
          }}
        >
          {step === "processing" && (
            <LoadingView
              error={error}
              onRetry={handleRetry}
              isProcessing={isProcessing}
            />
          )}

          {step === "success" && (
            <Box>
              {/* Mobile: Swipeable Cards */}
              {isMobile ? (
                <Box>
                  <Box
                    sx={{
                      overflow: "hidden",
                      position: "relative",
                      height: "350px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        transform: `translateX(-${currentSlide * 100}%)`,
                        transition: "transform 0.3s ease-in-out",
                        height: "100%",
                      }}
                    >
                      {slides.map((slide, index) => (
                        <Box
                          key={slide.type}
                          sx={{
                            minWidth: "100%",
                            px: 1,
                            animation:
                              index === currentSlide
                                ? "fadeInUp 0.5s ease-out"
                                : "none",
                          }}
                        >
                          {slide.type === "qr" && qrCodeData && (
                            <QRCodeCard
                              qrCodeData={qrCodeData}
                              canvasRef={qrCanvasRef}
                              isMobile={true}
                            />
                          )}
                          {slide.type === "pdf" && pdfUrl && (
                            <PDFCard
                              pdfUrl={pdfUrl}
                              onShare={handleShare}
                              isMobile={true}
                            />
                          )}
                          {slide.type === "finish" && (
                            <FinishCard onClose={onClose} isMobile={true} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <MobileNavigation
                    currentSlide={currentSlide}
                    totalSlides={slides.length}
                    onPrevious={() => handleSlideChange("prev")}
                    onNext={() => handleSlideChange("next")}
                  />

                  {/* Swipe Hint */}
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      <SwipeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Nach links/rechts wischen oder Pfeile verwenden
                    </Typography>
                  </Box>
                </Box>
              ) : (
                /* Desktop: Grid Layout */
                <Grow in={true} timeout={800}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 3,
                      mt: 2,
                    }}
                  >
                    {qrCodeData && (
                      <QRCodeCard
                        qrCodeData={qrCodeData}
                        canvasRef={qrCanvasRef}
                        isMobile={false}
                      />
                    )}
                    {pdfUrl && (
                      <PDFCard
                        pdfUrl={pdfUrl}
                        onShare={handleShare}
                        isMobile={false}
                      />
                    )}
                    <FinishCard onClose={onClose} isMobile={false} />
                  </Box>
                </Grow>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EndRentalModal;
